import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useStudentAuth } from "../../context/StudentAuthContext";
import logo from "../../assets/logo.png";

export default function StudentVerifyOtp() {
  const { verifyOtp, resendOtp } = useStudentAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!email) navigate("/student/signup", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await verifyOtp({ email, otp: otp.trim() });
      const dest = location.state?.from?.pathname || "/student/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.friendlyMessage || "Could not verify that code.");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResendState("sending");
    try {
      await resendOtp({ email });
      setResendState("sent");
      setCooldown(30);
    } catch {
      setResendState("idle");
    }
  }

  return (
    <div className="pm-auth-shell">
      <Link to="/" className="pm-auth-shell__back pm-btn pm-btn--ghost pm-btn--sm">
        ← Back to home
      </Link>
      <motion.div className="pm-auth-card" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Link to="/" className="pm-auth-brand">
          <img src={logo} alt="Positive Moves" className="pm-brand-logo" />
        </Link>
        <h1>Check your email</h1>
        <p className="pm-auth-sub">
          We've sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
        </p>

        <form onSubmit={handleSubmit} className="pm-auth-form">
          <div className="pm-field">
            <label>Verification code</label>
            <input
              inputMode="numeric"
              autoFocus
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              style={{ letterSpacing: "0.3em", fontSize: 20, textAlign: "center" }}
            />
          </div>
          {error && <div className="pm-form-error">{error}</div>}
          <button className="pm-btn pm-btn--primary pm-btn--lg" disabled={loading || otp.length !== 6} type="submit">
            {loading ? "Verifying…" : "Verify & continue"}
          </button>
        </form>

        <p className="pm-auth-switch">
          Didn't get it?{" "}
          <button
            type="button"
            className="pm-link-btn"
            onClick={handleResend}
            disabled={resendState === "sending" || cooldown > 0}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : resendState === "sending" ? "Sending…" : "Resend code"}
          </button>
        </p>
      </motion.div>
    </div>
  );
}